// Cajón de generación documental de una carga.
//
// El gesto y el cromado son los del cajón de adjuntos a propósito: en esta misma
// pantalla el usuario ya abre un panel lateral para ver documentos, y aquí abre
// uno para producirlos. La consistencia gana a la novedad.
//
// La pantalla tiene dos estados y el orden importa:
//
//   1. DATOS      — qué va a salir en el PDF, qué falta, y los campos que el
//                   usuario completa. Se revisa aquí, antes de generar.
//   2. PREVIEW    — el PDF renderizado, con descargar / volver / regenerar.
//
// Volver de preview a datos NO borra el formulario: corregir un incoterm y
// regenerar es el ciclo normal, no una excepción.
//
// Estos datos aún no se persisten. Se dice en pantalla en vez de dejar que el
// usuario lo descubra al reabrir el cajón.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader } from '@/components/common/Loader'
import { PanelHsCode } from './documentos/PanelHsCode'
import { PanelMultimodal } from './documentos/PanelMultimodal'
import { PanelAlbaranRecogida } from './documentos/PanelAlbaranRecogida'
import {
  BloqueAvisos,
  Celda,
  Formulario,
  CeldaEditable,
  Chip,
  OrigenBadge,
  OrigenSelector,
  PieGenerar,
  Seccion,
  TipoBoton,
  fmtEnt,
  fmtKg,
  mensajeDeError,
} from './documentos/piezas'
import { Drawer } from '@/components/common/Drawer'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useAlbaranRecogida } from '@/hooks/useAlbaranRecogida'
import type { ContextoAlbaran } from '@/utils/albaranRecogida'
import { documentosApi } from '@/services/api'
import type {
  CargaEstadoPicking,
  DocumentoAviso,
  DocumentoOrigen,
  DocumentoGenerado,
  PackingListCabeceraEditable,
  PackingListDatos,
  PackingListLinea,
  TipoPaletizado,
} from '@/types'
import { base64ToBlob, descargarBase64 } from '@/utils/base64'
import {
  aplicarCorreccion,
  camposCorregidos,
  camposPropagables,
  claveLinea,
  clavesDelArticulo,
  correccionesParaEnviar,
  lineasFinales,
  paletsEstimados,
  recalcularNivelesAereos,
  totalesPacking,
  unidadesLinea,
  type CampoEditable,
  type CorreccionLinea,
} from '@/utils/packingList'
import { fmtQty } from './formato'

/** Empresas cuyo packing list sabemos generar hoy.
 *
 *  Para avi/tdis DocGen usa la plantilla `packing`, que es otro contrato: plano,
 *  en inglés y agrupado por palet con SSCC. Necesita su propio SQL, así que no se
 *  sirve a medias. El backend también lo rechaza: ocultar el botón no es cerrar
 *  la puerta. */
/** Empresas cuyo emisor conoce DocGen.
 *
 *  Ya no es sólo `avp`: la plantilla `packing` (agrupada por palet) cubre a las
 *  demás, y el multimodal SÓLO existe para `avi` y `tdis` — el maestro de
 *  mercancía peligrosa está vacío en `avp` (medido: 2.323 artículos en avi,
 *  1.925 en tdis, 0 en avp). Dejar el cajón en `avp` habría dejado el
 *  multimodal sin ninguna carga que lo pudiera generar. */
export const EMPRESAS_CON_PACKING = new Set(['avp', 'avi', 'tdis', 'uniq'])

/** Los cuatro documentos del cajón.
 *
 *  Comparten carga, cabecera y formulario; se diferencian en el GRANO de sus
 *  líneas y en qué las agrupa:
 *
 *  - `packingList` — (matrícula × artículo), agrupado por bulto.
 *  - `hsCode`      — un LOTE por fila, agrupado por (país de origen, código HS).
 *  - `multimodal`  — las líneas del packing, agrupadas por CLASIFICACIÓN de
 *                    mercancía peligrosa. Sin consulta propia.
 *  - `albaranRecogida` — el único con FORMULARIO: ocho de sus 19 campos los
 *                    teclea una persona. Tiene su propio GET y su propio estado.
 */
const TIPOS = ['packingList', 'hsCode', 'multimodal', 'albaranRecogida'] as const

type TipoDocumento = typeof TIPOS[number]

const MIME_PDF = 'application/pdf'

/** Campos del formulario, en el orden en que se rellenan de verdad: primero lo
 *  administrativo, luego la ruta, luego las partes. */
const CAMPOS: { clave: keyof PackingListCabeceraEditable; tipo?: 'date' }[] = [
  { clave: 'facturaComercial' },
  { clave: 'fecha', tipo: 'date' },
  { clave: 'paisOrigen' },
  { clave: 'paisDestino' },
  { clave: 'incoterm' },
  { clave: 'navioVuelo' },
  { clave: 'contenedor' },
  { clave: 'destinatarioNombre' },
  { clave: 'notifyParty' },
  { clave: 'pickupLocation' },
]

interface Props {
  dark: boolean
  empresa: string
  numPedido: string
  carga: string
  /** Para pintar la cabecera antes de que lleguen los datos. */
  estado?: CargaEstadoPicking
  paletizada?: boolean | null
  /** Lo sabe ya la lista de cargas, así que el rótulo no espera a la consulta.
   *  La cabecera del packing trae el mismo dato (`esCargaAerea`) y manda a la
   *  hora de generar; aquí sólo adelanta lo que se va a ver. */
  aerea?: boolean
  /** Lo que la carga ya sabe donde se abre el cajón (pedido, cliente, destino,
   *  tipo de palet). Precarga el albarán de recogida sin otra llamada. */
  contextoAlbaran?: ContextoAlbaran
  onClose: () => void
}

/** Lo que necesita el contenido: lo mismo que el cajón, pero el cierre llega ya
 *  animado (`onCerrar`) en vez de ser el desmontaje del padre. */
type PanelProps = Omit<Props, 'onClose'> & { onCerrar: () => void }

// ── Formateo ──────────────────────────────────────────────────────────────────



// ── Componente principal ──────────────────────────────────────────────────────

/** El cajón de documentos de una carga. Envuelve a `CargaDocumentosPanel` con el
 *  `Drawer` común: Pedidos de venta y Cargas de salida lo abren igual que antes. */
export function CargaDocumentosDrawer({ onClose, ...resto }: Props) {
  const { t } = useTranslation()
  return (
    <Drawer
      open
      onClose={onClose}
      dark={resto.dark}
      width="w-screen sm:w-[min(96vw,1520px)]"
      ariaLabel={`${t('salesOrders.documentos.title')} — ${resto.carga}`}
    >
      {cerrar => <CargaDocumentosPanel {...resto} onCerrar={cerrar} />}
    </Drawer>
  )
}

/** Cabecera + raíl de tipos + panel de cada documento. Sin el marco del cajón:
 *  quien lo monte pone el contenedor (hoy, `CargaDocumentosDrawer`). */
export function CargaDocumentosPanel({
  dark, empresa, numPedido, carga, estado, paletizada, aerea, contextoAlbaran, onCerrar,
}: PanelProps) {
  const { t, i18n } = useTranslation()
  const [tipo, setTipo] = useState<TipoDocumento>('packingList')
  const [form, setForm] = useState<PackingListCabeceraEditable>({})
  const [pdf, setPdf] = useState<DocumentoGenerado | null>(null)
  const [errorGen, setErrorGen] = useState<string | null>(null)
  // `null` = manda el criterio automático del backend. El usuario lo fuerza
  // cuando el dato de almacén no es de nivel palet (un contenedor para toda la
  // carga da un palet con el peso del envío entero).
  const [origenForzado, setOrigenForzado] = useState<DocumentoOrigen | null>(null)
  // Correcciones manuales, por artículo. Los datos maestros de D365 fallan y el
  // usuario tiene que poder arreglarlos antes de mandar el documento.
  const [correcciones, setCorrecciones] = useState<Record<string, CorreccionLinea>>({})

  const idioma: 'es' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'es'

  const albaran = useAlbaranRecogida({
    empresa, carga, activo: tipo === 'albaranRecogida', onGenerado: (p) => setPdf(p),
    // Si el origen no aporta pedido, el de la cabecera del cajón es el mismo.
    contexto: { pedido: numPedido, ...contextoAlbaran },
  })

  /** Precarga el formulario solo si aún no se ha rellenado: al cambiar de fuente
   *  de palets vuelven los datos y no se puede perder lo que el usuario escribió. */
  const setFormSiVacio = (inicial: PackingListCabeceraEditable) =>
    setForm(actual => (Object.keys(actual).length === 0 ? inicial : actual))

  const { data, isLoading, isError } = useQuery({
    queryKey: ['packing-list-datos', empresa, carga, origenForzado],
    queryFn: () => documentosApi.getPackingListDatos(empresa, carga, origenForzado ?? undefined),
    staleTime: 2 * 60 * 1000,
    retry: 1,
    // El diálogo de guardado le quita el foco a la ventana; al volver, un
    // refetch reemplazaría los datos —y las correcciones a medio escribir— justo
    // cuando el usuario está eligiendo carpeta.
    refetchOnWindowFocus: false,
  })
  const datos: PackingListDatos | undefined = data?.data

  // El formulario arranca con lo que trae D365. Solo al llegar los datos: si se
  // reinicializara en cada render se borraría lo que el usuario está escribiendo.
  useEffect(() => {
    if (!datos) return
    const c = datos.cabecera
    setFormSiVacio({
      fecha: c.fecha ?? '',
      facturaComercial: c.facturaComercial ?? '',
      paisOrigen: c.paisOrigen ?? '',
      paisDestino: c.paisDestino ?? '',
      incoterm: c.incoterm ?? '',
      navioVuelo: c.navioVuelo ?? '',
      contenedor: c.contenedor ?? '',
      notifyParty: c.notifyParty ?? '',
      pickupLocation: c.pickupLocation ?? '',
      destinatarioNombre: c.destinatarioNombre ?? '',
      tipoPaletizado: c.tipoPaletizado ?? null,
    })
    // Depende de la CARGA y no de `datos`: cambiar la fuente de palets devuelve
    // un objeto nuevo, y con `datos` en las dependencias se borraría lo que el
    // usuario acabase de teclear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa, carga, datos?.cabecera.carga])

  /** Editar una celda la FIJA automáticamente: lo que el usuario escribe se
   *  queda, y la cascada no se lo pelea. El candado se puede soltar después.
   *
   *  Los campos que son del ARTÍCULO y no del bulto (código HS, uds/caja,
   *  descripción, y los pesos por caja en marítimo) se propagan a todas las
   *  líneas de esa referencia: con el grano nuevo el mismo artículo sale hasta
   *  200 veces, y corregir su HS bulto a bulto no tiene sentido. */
  function corregir(clave: string, campo: CampoEditable, valor: string) {
    const linea = (datos?.lineas ?? []).find(l => claveLinea(l) === clave)
    const propaga = linea && camposPropagables(datos?.cabecera.esCargaAerea === 1).includes(campo)
    const claves = propaga
      ? clavesDelArticulo(datos!.lineas, linea!.articulo)
      : [clave]

    setCorrecciones(prev => {
      const siguiente = { ...prev }
      for (const k of claves) {
        const actual = siguiente[k] ?? { valores: {}, fijados: [], excluida: false }
        siguiente[k] = {
          ...actual,
          valores: { ...actual.valores, [campo]: valor },
          fijados: actual.fijados.includes(campo) ? actual.fijados : [...actual.fijados, campo],
        }
      }
      return siguiente
    })
  }

  function alternarFijado(clave: string, campo: CampoEditable) {
    setCorrecciones(prev => {
      const actual = prev[clave] ?? { valores: {}, fijados: [], excluida: false }
      const fijados = actual.fijados.includes(campo)
        ? actual.fijados.filter(c => c !== campo)
        : [...actual.fijados, campo]
      return { ...prev, [clave]: { ...actual, fijados } }
    })
  }

  function alternarExcluida(clave: string) {
    setCorrecciones(prev => {
      const actual = prev[clave] ?? { valores: {}, fijados: [], excluida: false }
      return { ...prev, [clave]: { ...actual, excluida: !actual.excluida } }
    })
  }

  // Cambiar de fuente de datos trae otras líneas: las correcciones dejan de tener
  // sentido y arrastrarlas pondría valores de una fuente sobre la otra.
  function cambiarOrigen(o: DocumentoOrigen | null) {
    setOrigenForzado(o)
    setCorrecciones({})
  }

  const originales = datos?.lineas ?? []
  const lineasCorregidas = useMemo(
    () => lineasFinales(originales, correcciones),
    [originales, correcciones],
  )

  /** Cuántos valores ha tocado una persona. Se enseña en el pie: un documento
   *  corregido a mano no puede parecer uno que salió tal cual del sistema. */
  const numCorregidos = useMemo(
    () => originales.reduce((n, o) => {
      const c = correcciones[claveLinea(o)]
      if (!c || c.excluida) return n
      return n + camposCorregidos(o, aplicarCorreccion(o, c)).size
    }, 0),
    [originales, correcciones],
  )
  const numExcluidas = originales.filter(o => correcciones[claveLinea(o)]?.excluida).length
  const hayCorrecciones = numCorregidos > 0 || numExcluidas > 0

  // En aéreo, la corrección de una línea sube por los tres niveles. Espejo de lo
  // que hace el backend al generar: si la pantalla no lo reflejara, el usuario
  // revisaría una banda de indicadores que ya no corresponde a sus líneas.
  const datosCorregidos: PackingListDatos | undefined = useMemo(() => {
    if (!datos) return undefined
    const base = { ...datos, lineas: lineasCorregidas }
    if (datos.cabecera.esCargaAerea !== 1 || !hayCorrecciones) return base
    return { ...base, ...recalcularNivelesAereos(
      lineasCorregidas, datos.airPalets, datos.airBoxes, datos.resumen) }
  }, [datos, lineasCorregidas, hayCorrecciones])

  const generar = useMutation({
    // Se manda el origen que el usuario tiene delante, no el automático: el PDF
    // tiene que salir con las cifras que acaba de revisar.
    mutationFn: () => documentosApi.generarPackingList({
      empresa, carga, idioma, cabecera: form, origen: datos?.origen,
      // Solo lo que de verdad cambió, con el artículo como clave. El backend
      // relee las líneas de Fabric y aplica esto encima, registrando el
      // antes→después de cada campo en auditoría.
      lineas: correccionesParaEnviar(originales, correcciones),
    }),
    onSuccess: (r) => { setPdf(r.data); setErrorGen(null) },
    onError: (e: unknown) => {
      setErrorGen(mensajeDeError(e) ?? t('salesOrders.documentos.errorGenerar'))
    },
  })

  // Los avisos del backend se calcularon sobre el dato crudo. En cuanto hay
  // correcciones se filtran los de artículos ya tocados o excluidos: dejar
  // «falta el peso» de una línea cuyo peso el usuario acaba de escribir es
  // pedirle que arregle algo que ya arregló. El backend los revalida al generar
  // y es él quien decide de verdad.
  const tocados = new Set(
    originales.filter(o => {
      const c = correcciones[claveLinea(o)]
      return c && (c.excluida || camposCorregidos(o, aplicarCorreccion(o, c)).size > 0)
    }).map(o => o.articulo),
  )
  const vigentes = (datos?.avisos ?? []).filter(a => !a.articulo || !tocados.has(a.articulo))

  // Regla bloqueante, recalculada en local para que el botón responda al vuelo:
  // sin uds/caja no hay cajas ni pesos y el documento saldría inventado. Es la
  // única que impide generar, así que es la única que hace falta duplicar.
  const sinUdsCaja = lineasCorregidas.filter(l => !l.unidadesCaja)
  const bloqueantes = hayCorrecciones
    ? sinUdsCaja.map(l => ({ codigo: 'sin_unidades_caja', bloqueante: true, articulo: l.articulo }))
    : vigentes.filter(a => a.bloqueante)
  const informativos = vigentes.filter(a => !a.bloqueante)
  const puedeGenerar = !!datos && lineasCorregidas.length > 0 && bloqueantes.length === 0

  // ── Tema (mismas variables que el cajón de adjuntos) ───────────────────────
  const railBg    = dark ? 'bg-[#161b22]'        : 'bg-slate-50'
  const border    = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const mainTxt   = dark ? 'text-slate-100'      : 'text-slate-900'
  const mutedTxt  = dark ? 'text-slate-400'      : 'text-slate-500'
  const dimTxt    = dark ? 'text-slate-600'      : 'text-slate-400'

  return (
    <>
        {/* ── Cabecera ──────────────────────────────────────────────────────── */}
        <div className={`flex items-center gap-4 px-6 py-4 border-b ${border} shrink-0`}>
          <div className={`p-2 rounded-lg ${dark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <svg className={`w-4 h-4 ${mutedTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6M9 15h6M9 11h3" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[13px] font-semibold ${mainTxt}`}>
              {t('salesOrders.documentos.title')}
            </h2>
            <p className={`text-[11px] mt-0.5 flex items-center gap-2 flex-wrap ${mutedTxt}`}>
              <span className="font-mono font-semibold">{carga}</span>
              <span className={dimTxt}>·</span>
              <span className="font-mono">{numPedido}</span>
              {estado && <Chip texto={t(`salesOrders.picking.estado.${estado}`)} tono="neutro" dark={dark} />}
              {paletizada != null && (
                <Chip
                  texto={t(paletizada ? 'salesOrders.picking.paletizada' : 'salesOrders.picking.noPaletizada')}
                  tono={paletizada ? 'indigo' : 'contorno'}
                  dark={dark}
                />
              )}
              {(aerea || datos?.cabecera.esCargaAerea === 1) && (
                <Chip texto={t('salesOrders.documentos.aerea')} tono="violeta" dark={dark} />
              )}
              {tipo === 'packingList' && datos && datos.cabecera.esCargaAerea !== 1 && (
                <OrigenBadge origen={datos.origen} dark={dark} />
              )}
            </p>
          </div>
          <button
            onClick={onCerrar}
            aria-label={t('salesOrders.documentos.cerrar')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors cursor-pointer ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6'
                   : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span className={`text-[10px] font-mono hidden sm:inline ${dimTxt}`}>ESC</span>
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* ── Raíl de tipos de documento ─────────────────────────────────── */}
          <nav className={`hidden md:flex flex-col w-[196px] shrink-0 border-r ${border} ${railBg} py-3`}>
            {TIPOS.map(clave => (
              <TipoBoton
                key={clave}
                clave={clave}
                activo={clave === tipo}
                dark={dark}
                onClick={() => { setTipo(clave); setPdf(null); setErrorGen(null) }}
              />
            ))}
            <div className={`mt-auto px-4 pt-3 border-t ${border}`}>
              <p className={`text-[9px] font-bold uppercase tracking-wider ${dimTxt}`}>
                {t('salesOrders.documentos.emisor')}
              </p>
              <p className={`text-[11px] font-mono font-semibold mt-0.5 ${mutedTxt}`}>
                {empresa.toUpperCase()}
              </p>
            </div>
          </nav>

          {/* ── Panel principal ───────────────────────────────────────────────*/}
          <div className="flex flex-col flex-1 min-w-0">
            {/* El HS code tiene su propia consulta y pinta sus propios estados;
                estos son los de las líneas del packing, que el multimodal
                comparte. */}
            {tipo !== 'hsCode' && tipo !== 'albaranRecogida' && isLoading && (
              <div className="flex-1 flex items-center justify-center">
                <Loader label={t('salesOrders.documentos.cargandoDatos')} />
              </div>
            )}

            {tipo !== 'hsCode' && tipo !== 'albaranRecogida' && isError && (
              <div className="flex-1 flex items-center justify-center px-8">
                <p className={`text-[12px] text-center ${mutedTxt}`}>
                  {t('salesOrders.documentos.errorDatos')}
                </p>
              </div>
            )}

            {tipo === 'hsCode' && !pdf && (
              <PanelHsCode
                dark={dark}
                empresa={empresa}
                carga={carga}
                idioma={idioma}
                form={form}
                onCambioForm={(clave, valor) => setForm(f => ({ ...f, [clave]: valor }))}
                onGenerado={setPdf}
              />
            )}

            {tipo === 'albaranRecogida' && !pdf && (
              <PanelAlbaranRecogida dark={dark} albaran={albaran} />
            )}

            {tipo === 'multimodal' && datos && !pdf && (
              <PanelMultimodal
                dark={dark}
                empresa={empresa}
                carga={carga}
                idioma={idioma}
                // Las corregidas, no las crudas: es la misma fuente que el
                // packing, así que un peso corregido allí tiene que llegar aquí.
                datos={datosCorregidos ?? datos}
                correcciones={correccionesParaEnviar(originales, correcciones)}
                form={form}
                onCambioForm={(clave, valor) => setForm(f => ({ ...f, [clave]: valor }))}
                onGenerado={setPdf}
              />
            )}

            {tipo === 'packingList' && datos && !pdf && (
              <PanelDatos
                dark={dark}
                datos={datosCorregidos ?? datos}
                originales={originales}
                correcciones={correcciones}
                form={form}
                onCambio={(clave, valor) => setForm(f => ({ ...f, [clave]: valor }))}
                onOrigen={cambiarOrigen}
                onCorregir={corregir}
                onFijar={alternarFijado}
                onExcluir={alternarExcluida}
                onRestablecer={() => setCorrecciones({})}
                numCorregidos={numCorregidos}
                numExcluidas={numExcluidas}
                bloqueantes={bloqueantes}
                informativos={informativos}
                puedeGenerar={puedeGenerar}
                generando={generar.isPending}
                errorGen={errorGen}
                onGenerar={() => generar.mutate()}
              />
            )}

            {pdf && (
              <PanelPreview
                dark={dark}
                pdf={pdf}
                regenerando={tipo === 'albaranRecogida' ? albaran.generar.isPending : generar.isPending}
                onVolver={() => { setPdf(null); setErrorGen(null) }}
                // «Regenerar» SOLO para los tipos cuyo generador vive en este
                // componente. Antes se llamaba a `generar` sin mirar el tipo, y
                // `generar` es el del PACKING LIST: quien generaba el listado de
                // códigos HS o el multimodal y pulsaba aquí se llevaba un packing
                // list, con el mismo cajón y sin un solo aviso. El HS y el
                // multimodal generan dentro de su propio panel y este componente
                // no tiene forma de repetirlo, así que no se ofrece el botón:
                // ninguno es mejor que uno que devuelve otro documento. Para
                // regenerarlos se vuelve con «Volver», que sí funciona.
                onRegenerar={
                  tipo === 'albaranRecogida' ? () => albaran.generar.mutate()
                  : tipo === 'packingList'   ? () => generar.mutate()
                  : undefined
                }
              />
            )}
          </div>
        </div>
    </>
  )
}

// ── Panel de datos ────────────────────────────────────────────────────────────

function PanelDatos({
  dark, datos, originales, correcciones, form, onCambio, onOrigen,
  onCorregir, onFijar, onExcluir, onRestablecer,
  bloqueantes, informativos, numCorregidos, numExcluidas,
  puedeGenerar, generando, errorGen, onGenerar,
}: {
  dark: boolean
  datos: PackingListDatos
  originales: PackingListLinea[]
  correcciones: Record<string, CorreccionLinea>
  form: PackingListCabeceraEditable
  onCambio: (clave: keyof PackingListCabeceraEditable, valor: string) => void
  onOrigen: (o: DocumentoOrigen | null) => void
  onCorregir: (articulo: string, campo: CampoEditable, valor: string) => void
  onFijar: (articulo: string, campo: CampoEditable) => void
  onExcluir: (articulo: string) => void
  onRestablecer: () => void
  numCorregidos: number
  numExcluidas: number
  bloqueantes: DocumentoAviso[]
  informativos: DocumentoAviso[]
  puedeGenerar: boolean
  generando: boolean
  errorGen: string | null
  onGenerar: () => void
}) {
  const { t } = useTranslation()

  if (generando) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader label={t('salesOrders.documentos.generando')} />
      </div>
    )
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <TablaContenido
          dark={dark}
          datos={datos}
          originales={originales}
          correcciones={correcciones}
          tipoPaletizado={form.tipoPaletizado}
          onOrigen={onOrigen}
          onCorregir={onCorregir}
          onFijar={onFijar}
          onExcluir={onExcluir}
        />
        <BloqueAvisos dark={dark} bloqueantes={bloqueantes} informativos={informativos} />
        {datos.cabecera.estimadoPaletizado && (
          <SelectorPaletizado
            dark={dark}
            datos={datos}
            valor={form.tipoPaletizado}
            onCambio={v => onCambio('tipoPaletizado', v)}
          />
        )}
        <Formulario dark={dark} form={form} campos={CAMPOS} onCambio={onCambio} />
      </div>

      <PieGenerar
        dark={dark}
        errorGen={errorGen}
        bloqueantes={bloqueantes.length}
        listas={t('salesOrders.documentos.lineasListas', { count: datos.lineas.length })}
        numCorregidos={numCorregidos}
        numExcluidas={numExcluidas}
        onRestablecer={onRestablecer}
        puedeGenerar={puedeGenerar}
        onGenerar={onGenerar}
      />
    </>
  )
}

// ── Tipo de paletizado (estimado AVP) ────────────────────────────────────────

/** Mixto o monorreferencial: qué estimación de palets declara el packing AVP
 *  estimado de una carga paletizada. `permite_palet_mixto` está mal informado
 *  en origen, así que lo elige quien emite; arranca en mono, el conservador.
 *  Un tipo sin recuento se deshabilita: el backend lo sacaría no paletizado. */
function SelectorPaletizado({
  dark, datos, valor, onCambio,
}: {
  dark: boolean
  datos: PackingListDatos
  valor?: TipoPaletizado | null
  onCambio: (v: TipoPaletizado) => void
}) {
  const { t } = useTranslation()
  const c = datos.cabecera
  const activo = valor ?? c.tipoPaletizado ?? 'mono'
  const opciones: { tipo: TipoPaletizado; palets?: number | null; tara?: number | null }[] = [
    { tipo: 'mono', palets: c.paletsEstimadosMono, tara: c.pesoPaletsMono },
    { tipo: 'mixto', palets: c.paletsEstimadosMixto, tara: c.pesoPaletsMixto },
  ]
  return (
    <Seccion
      titulo={t('salesOrders.documentos.paletizado.titulo')}
      nota={t('salesOrders.documentos.paletizado.nota')}
      dark={dark}
    >
      <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {opciones.map(o => {
          const sel = o.tipo === activo
          const sinDato = !o.palets
          return (
            <button
              key={o.tipo}
              type="button"
              role="radio"
              aria-checked={sel}
              disabled={sinDato}
              onClick={() => onCambio(o.tipo)}
              className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                sinDato ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } ${sel
                ? (dark ? 'border-sky-400 bg-sky-500/10' : 'border-sky-600 bg-sky-50')
                : (dark ? 'border-white/10 hover:bg-white/[0.04]' : 'border-slate-200 hover:bg-slate-50')}`}
            >
              <span className={`block text-[12px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                {t(`salesOrders.documentos.paletizado.${o.tipo}`)}
              </span>
              <span className={`block text-[11px] tabular-nums ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                {sinDato
                  ? t('salesOrders.documentos.paletizado.sinDato')
                  : t('salesOrders.documentos.paletizado.detalle', {
                      count: o.palets ?? 0, tara: fmtKg(o.tara ?? null),
                    })}
              </span>
            </button>
          )
        })}
      </div>
    </Seccion>
  )
}

// ── Tabla de contenido ────────────────────────────────────────────────────────

/** Un grupo de líneas bajo una misma matrícula (o air-box). */
interface Grupo {
  clave: string
  matricula: string | null
  airPalet: string | null
  lineas: { original: PackingListLinea; final: PackingListLinea; indice: number }[]
}

/** Agrupa las líneas por matrícula conservando el orden de aparición.
 *
 *  El orden importa: el contrato numera la columna «Línea» siguiendo el recorrido
 *  del array, y los procedures ya devuelven el ORDER BY que agrupa. */
function agrupar(
  originales: PackingListLinea[],
  finalPorClave: Map<string, PackingListLinea>,
): Grupo[] {
  const grupos = new Map<string, Grupo>()
  originales.forEach((original, i) => {
    const clave = original.matriculaPalet ?? ''
    let g = grupos.get(clave)
    if (!g) {
      g = {
        clave,
        matricula: original.matriculaPalet ?? null,
        airPalet: original.airPalet ?? null,
        lineas: [],
      }
      grupos.set(clave, g)
    }
    g.lineas.push({
      original,
      final: finalPorClave.get(claveLinea(original)) ?? original,
      indice: i + 1,
    })
  })
  return [...grupos.values()]
}

function TablaContenido({
  dark, datos, originales, correcciones, tipoPaletizado, onOrigen, onCorregir, onFijar, onExcluir,
}: {
  dark: boolean
  datos: PackingListDatos
  originales: PackingListLinea[]
  correcciones: Record<string, CorreccionLinea>
  tipoPaletizado?: TipoPaletizado | null
  onOrigen: (o: DocumentoOrigen | null) => void
  onCorregir: (clave: string, campo: CampoEditable, valor: string) => void
  onFijar: (clave: string, campo: CampoEditable) => void
  onExcluir: (clave: string) => void
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const aerea = datos.cabecera.esCargaAerea === 1
  const paletizado = datos.cabecera.noPaletizado === 0
  // Sólo lo que el usuario ha tocado; el resto sigue al criterio por defecto.
  // Mismo patrón que la tarjeta de cargas de la pantalla.
  const [plegadoManual, setPlegadoManual] = useState<Map<string, boolean>>(new Map())

  const totales = useMemo(() => totalesPacking(datos.lineas), [datos.lineas])
  // Estimado paletizado: los palets no se cuentan por matrícula (no hay), se
  // declaran; y su tara va al bruto del PDF. Se enseña aparte para que el
  // bruto por caja de la tabla siga siendo el del maestro.
  const estimacion = paletsEstimados(datos.cabecera, tipoPaletizado)
  const finalPorClave = useMemo(
    () => new Map(datos.lineas.map(l => [claveLinea(l), l])),
    [datos.lineas],
  )
  const grupos = useMemo(
    () => agrupar(originales, finalPorClave),
    [originales, finalPorClave],
  )
  const paletsAereos = useMemo(
    () => new Map(datos.airPalets.map(p => [p.airPalet, p])),
    [datos.airPalets],
  )
  const boxes = useMemo(
    () => new Map(datos.airBoxes.map(b => [b.airBox, b])),
    [datos.airBoxes],
  )

  const th = `px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
    dark ? 'text-slate-500' : 'text-slate-400'}`
  const td = `px-1.5 py-1 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const divider = dark ? 'border-white/8' : 'border-slate-200'
  const tfoot = dark ? 'bg-white/[0.04] text-slate-100' : 'bg-slate-100 text-slate-900'

  // Con pocos bultos, ver el detalle de golpe es lo cómodo. A partir de ahí la
  // tabla se vuelve ilegible —26APCG000040 son 200 bultos y 200 líneas—, así que
  // el detalle arranca plegado y el subtotal de cada bulto queda a la vista.
  const plegadoPorDefecto = grupos.length > 5
  const plegado = (clave: string) => plegadoManual.get(clave) ?? plegadoPorDefecto
  const alternar = (clave: string) =>
    setPlegadoManual(prev => new Map(prev).set(clave, !plegado(clave)))
  const todos = (valor: boolean) =>
    setPlegadoManual(new Map(grupos.map(g => [g.clave, valor])))
  const algunoAbierto = grupos.some(g => !plegado(g.clave))

  const botonPlegar = grupos.length > 1 && grupos.some(g => g.matricula) ? (
    <button
      type="button"
      onClick={() => todos(algunoAbierto)}
      className={`text-[10px] font-semibold underline underline-offset-2 cursor-pointer hover:no-underline ${
        dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
    >
      {t(algunoAbierto
        ? 'salesOrders.documentos.contenido.plegarTodo'
        : 'salesOrders.documentos.contenido.desplegarTodo')}
    </button>
  ) : null

  const selector = (
    <div className="flex items-center gap-3">
      {botonPlegar}
      {!aerea && (
        <OrigenSelector dark={dark} activo={datos.origen} auto={datos.origenAuto} onCambio={onOrigen} />
      )}
    </div>
  )

  if (originales.length === 0) {
    return (
      <Seccion titulo={t('salesOrders.documentos.contenido.title')} dark={dark} accion={selector}>
        <p className={`text-[12px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('salesOrders.documentos.contenido.vacio')}
        </p>
      </Seccion>
    )
  }

  // 8 columnas de línea + la de excluir. Las cabeceras de grupo las ocupan todas.
  const COLS = 9
  let ultimoAirPalet: string | null = null


  return (
    <Seccion
      titulo={t('salesOrders.documentos.contenido.title')}
      nota={t(aerea
        ? 'salesOrders.documentos.contenido.notaAerea'
        : 'salesOrders.documentos.contenido.nota')}
      dark={dark}
      accion={selector}
    >
      {aerea && <ResumenAereo dark={dark} datos={datos} />}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead>
            <tr className={`border-b ${divider}`}>
              <th className={th} />
              <th className={th}>{t('salesOrders.documentos.contenido.linea')}</th>
              <th className={th}>{t('salesOrders.documentos.contenido.articulo')}</th>
              <th className={th}>{t('salesOrders.documentos.contenido.hsCode')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.unidades')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.udsCaja')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.cajas')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.netoCaja')}</th>
              <th className={`${th} text-right`}>{t('salesOrders.documentos.contenido.brutoCaja')}</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map(grupo => {
              const filas = []

              // Nivel 1 (solo aéreo): el air-palet, cuando cambia.
              if (aerea && grupo.airPalet && grupo.airPalet !== ultimoAirPalet) {
                ultimoAirPalet = grupo.airPalet
                const p = paletsAereos.get(grupo.airPalet)
                filas.push(
                  <FilaNivel
                    key={`pal-${grupo.airPalet}`}
                    dark={dark} divider={divider} cols={COLS} nivel={1}
                    titulo={t('salesOrders.documentos.contenido.airPalet')}
                    identidad={grupo.airPalet}
                    detalles={p ? [
                      [t('salesOrders.documentos.contenido.airBoxes'), fmtEnt(p.numAirBox)],
                      [t('salesOrders.documentos.contenido.neto'), fmtKg(p.pesoNeto)],
                      [t('salesOrders.documentos.contenido.tara'), fmtKg(p.tara)],
                      [t('salesOrders.documentos.contenido.bruto'), fmtKg(p.pesoBruto)],
                      ...(p.tipo ? [[t('salesOrders.documentos.control.tipoPalet'), p.tipo] as const] : []),
                    ] : []}
                  />,
                )
              }

              // Nivel 2: la matrícula (air-box en aéreo, palet en marítimo).
              const abierto = !grupo.matricula || !plegado(grupo.clave)
              if (grupo.matricula) {
                const b = boxes.get(grupo.matricula)
                const sub = totalesPacking(grupo.lineas.map(x => x.final))
                filas.push(
                  <FilaNivel
                    key={`mat-${grupo.clave}`}
                    dark={dark} divider={divider} cols={COLS} nivel={aerea ? 2 : 1}
                    abierto={abierto}
                    onAlternar={() => alternar(grupo.clave)}
                    nLineas={grupo.lineas.length}
                    titulo={t(aerea
                      ? 'salesOrders.documentos.contenido.airBox'
                      : 'salesOrders.documentos.contenido.palet')}
                    identidad={grupo.matricula}
                    detalles={[
                      ...(aerea && b
                        ? [[t('salesOrders.documentos.contenido.peso'), fmtKg(b.peso)] as const]
                        : []),
                      [t('salesOrders.documentos.contenido.cajas'), fmtEnt(sub.cajas)],
                      ...(aerea ? [] : [
                        [t('salesOrders.documentos.contenido.neto'), fmtKg(sub.neto)] as const,
                        [t('salesOrders.documentos.contenido.bruto'), fmtKg(sub.bruto)] as const,
                      ]),
                    ]}
                  />,
                )
              }

              // Nivel 3: las referencias. Plegadas, el subtotal del bulto sigue
              // a la vista en su cabecera, así que no se pierde información.
              for (const { original, final, indice } of (abierto ? grupo.lineas : [])) {
                filas.push(
                  <FilaLinea
                    key={claveLinea(original)}
                    original={original}
                    linea={final}
                    correccion={correcciones[claveLinea(original)]}
                    indice={indice}
                    sangrado={grupo.matricula ? (aerea ? 2 : 1) : 0}
                    dark={dark} td={td} divider={divider} fmtArt={fmtArt}
                    onCorregir={onCorregir} onFijar={onFijar} onExcluir={onExcluir}
                  />,
                )
              }
              return filas
            })}
          </tbody>
          <tfoot>
            <tr className={`border-t-2 ${divider} ${tfoot} font-semibold`}>
              <td className={td} colSpan={4}>
                {t('salesOrders.documentos.contenido.totales')}
                {paletizado && (
                  <span className={`ml-2 font-normal ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {t('salesOrders.documentos.contenido.paletsCount', {
                      count: estimacion ? estimacion.palets : totales.palets,
                    })}
                  </span>
                )}
              </td>
              <td className={`${td} text-right tabular-nums`}>{fmtQty(totales.unidades)}</td>
              <td className={td} />
              <td className={`${td} text-right tabular-nums`}>{fmtQty(totales.cajas)}</td>
              <td className={`${td} text-right tabular-nums`}>{fmtKg(totales.neto)}</td>
              <td className={`${td} text-right tabular-nums`}>{fmtKg(totales.bruto)}</td>
            </tr>
            {estimacion && (
              <>
                <tr className={tfoot}>
                  <td className={td} colSpan={8}>
                    {t('salesOrders.documentos.paletizado.taraFila', {
                      tipo: t(`salesOrders.documentos.paletizado.${estimacion.tipo}`),
                      count: estimacion.palets,
                    })}
                  </td>
                  <td className={`${td} text-right tabular-nums`}>{fmtKg(estimacion.tara)}</td>
                </tr>
                <tr className={`${tfoot} font-semibold`}>
                  <td className={td} colSpan={8}>{t('salesOrders.documentos.paletizado.brutoConPalets')}</td>
                  <td className={`${td} text-right tabular-nums`}>
                    {fmtKg(totales.bruto + (estimacion.tara ?? 0))}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      <BloqueControl dark={dark} lineas={datos.lineas} />
    </Seccion>
  )
}

/** Cifras de nivel carga en aéreo. Van arriba y una sola vez, que es como el
 *  contrato las trata: repetirlas por línea y sumarlas daría 929.214 kg en vez de
 *  4.646,07 en una carga real. */
function ResumenAereo({ dark, datos }: { dark: boolean; datos: PackingListDatos }) {
  const { t } = useTranslation()
  const r = datos.resumen
  const cls = dark
    ? 'bg-sky-500/10 border-sky-500/25 text-sky-200'
    : 'bg-sky-50 border-sky-200 text-sky-900'
  const dato = (etiqueta: string, valor: string | null) => (
    <span key={etiqueta}>
      <span className="opacity-70">{etiqueta}: </span>
      <span className="font-semibold tabular-nums">{valor ?? '—'}</span>
    </span>
  )
  return (
    <div className={`mb-2 rounded-lg border px-3 py-2 text-[11px] flex flex-wrap gap-x-4 gap-y-1 ${cls}`}>
      {dato(t('salesOrders.documentos.contenido.airPalets'), fmtEnt(r.numAirPalet))}
      {dato(t('salesOrders.documentos.contenido.airBoxes'), fmtEnt(r.numAirBox))}
      {dato(t('salesOrders.documentos.contenido.neto'), fmtKg(r.pesoNetoCarga))}
      {dato(t('salesOrders.documentos.contenido.bruto'), fmtKg(r.pesoBrutoCarga))}
    </div>
  )
}

/** Cabecera de un nivel de agrupación: air-palet, air-box o palet. */
function FilaNivel({
  dark, divider, cols, nivel, titulo, identidad, detalles,
  abierto, onAlternar, nLineas,
}: {
  dark: boolean
  divider: string
  cols: number
  nivel: 1 | 2
  titulo: string
  identidad: string
  detalles: readonly (readonly [string, string | null])[]
  // Sólo las cabeceras que tienen líneas debajo se pliegan; el air-palet agrupa
  // air-boxes, que ya son cabeceras, y se queda siempre visible.
  abierto?: boolean
  onAlternar?: () => void
  nLineas?: number
}) {
  // El nivel 1 pesa más que el 2: son dos jerarquías distintas y tienen que
  // distinguirse de un vistazo, no leyendo el rótulo.
  const fondo = nivel === 1
    ? (dark ? 'bg-white/[0.07]' : 'bg-slate-200/70')
    : (dark ? 'bg-white/[0.03]' : 'bg-slate-100/70')
  const texto = dark ? 'text-slate-200' : 'text-slate-700'
  const plegable = !!onAlternar

  const contenido = (
    <span className={nivel === 2 ? 'pl-5 inline-block' : ''}>
      {plegable && (
        <span className={`inline-block w-3 text-center font-mono ${
          dark ? 'text-slate-500' : 'text-slate-400'}`}>{abierto ? '▾' : '▸'}</span>
      )}
      <span className={`uppercase tracking-wider font-bold ${
        dark ? 'text-slate-500' : 'text-slate-400'} ${plegable ? 'ml-1' : ''}`}>{titulo}</span>
      <span className="ml-2 font-mono font-semibold">{identidad}</span>
      {detalles.map(([etiqueta, valor]) => (
        <span key={etiqueta} className="ml-3 opacity-80">
          {etiqueta} <span className="font-semibold tabular-nums">{valor ?? '—'}</span>
        </span>
      ))}
      {plegable && !abierto && nLineas != null && (
        <span className={`ml-3 opacity-70 tabular-nums ${
          dark ? 'text-slate-400' : 'text-slate-500'}`}>({nLineas})</span>
      )}
    </span>
  )

  return (
    <tr className={`border-t ${divider} ${fondo}`}>
      <td colSpan={cols} className={`px-2 py-1 text-[10px] ${texto}`}>
        {plegable ? (
          <button
            type="button"
            onClick={onAlternar}
            className="w-full text-left cursor-pointer hover:opacity-80"
          >
            {contenido}
          </button>
        ) : contenido}
      </td>
    </tr>
  )
}

function FilaLinea({
  original, linea: l, correccion, indice, sangrado, dark, td, divider, fmtArt,
  onCorregir, onFijar, onExcluir,
}: {
  original: PackingListLinea
  linea: PackingListLinea
  correccion?: CorreccionLinea
  indice: number
  sangrado: number
  dark: boolean
  td: string
  divider: string
  fmtArt: (a: string) => string
  onCorregir: (clave: string, campo: CampoEditable, valor: string) => void
  onFijar: (clave: string, campo: CampoEditable) => void
  onExcluir: (clave: string) => void
}) {
  const { t } = useTranslation()
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const clave = claveLinea(original)
  const excluida = !!correccion?.excluida
  const corregidos = useMemo(() => camposCorregidos(original, l), [original, l])
  const fijados = useMemo(() => new Set(correccion?.fijados ?? []), [correccion])

  const celda = (campo: CampoEditable, formato: (v: never) => string | null, derivado = false) => (
    <CeldaEditable
      dark={dark}
      td={td}
      campo={campo}
      valor={l[campo] as string | number | null | undefined}
      original={original[campo] as string | number | null | undefined}
      texto={formato(l[campo] as never)}
      corregido={corregidos.has(campo)}
      fijado={fijados.has(campo)}
      derivado={derivado}
      bloqueado={excluida}
      onCambio={v => onCorregir(clave, campo, v)}
      onFijar={() => onFijar(clave, campo)}
    />
  )

  return (
    <tr className={`border-t ${divider} ${excluida ? 'opacity-40' : ''}`}>
      <td className={`${td} w-8`}>
        <button
          type="button"
          onClick={() => onExcluir(clave)}
          title={t(excluida
            ? 'salesOrders.documentos.correcciones.incluirTooltip'
            : 'salesOrders.documentos.correcciones.excluirTooltip')}
          aria-pressed={excluida}
          className={`p-1 rounded transition-colors cursor-pointer ${
            excluida
              ? (dark ? 'text-red-400 hover:bg-white/8' : 'text-red-600 hover:bg-slate-200/70')
              : (dark ? 'text-slate-600 hover:text-slate-300 hover:bg-white/8'
                      : 'text-slate-300 hover:text-slate-600 hover:bg-slate-200/70')
          }`}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {excluida
              ? <path d="M3 12a9 9 0 1018 0 9 9 0 10-18 0M9 12h6" />
              : <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />}
          </svg>
        </button>
      </td>
      <td className={`${td} tabular-nums ${mutedTxt} ${excluida ? 'line-through' : ''}`}>
        {indice}
      </td>
      <td className={td}>
        <div className={`max-w-[240px] ${excluida ? 'line-through' : ''}`}
             style={{ paddingLeft: sangrado * 12 }}>
          <div className="font-semibold text-[11px] truncate" title={fmtArt(original.articulo)}>
            {fmtArt(original.articulo)}
          </div>
          <CeldaEditable
            dark={dark} td="" campo="descripcion"
            valor={l.descripcion} original={original.descripcion}
            texto={l.descripcion || null}
            corregido={corregidos.has('descripcion')}
            fijado={false} derivado={false} bloqueado={excluida} plano
            onCambio={v => onCorregir(clave, 'descripcion', v)}
            onFijar={() => {}}
          />
        </div>
      </td>
      {celda('hscode', v => (v as string) || null)}
      {celda('unidades', () => fmtQty(unidadesLinea(l)))}
      {celda('unidadesCaja', v => fmtEnt(v as number | null))}
      {celda('cantidadCajas', v => fmtEnt(v as number | null), true)}
      {celda('pesoCaja', v => fmtKg(v as number | null))}
      {celda('pesoBrutoCaja', v => fmtKg(v as number | null))}
    </tr>
  )
}



/** Columnas de control: no van al PDF, explican de dónde salen los pesos.
 *
 *  Van en un bloque aparte y no como columnas más: la tabla de arriba es «lo que
 *  saldrá en el documento» y mezclarlas rompería esa lectura. */
function BloqueControl({ dark, lineas }: { dark: boolean; lineas: PackingListLinea[] }) {
  const { t } = useTranslation()
  const [abierto, setAbierto] = useState(false)
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const divider = dark ? 'border-white/8' : 'border-slate-200'
  const th = `px-2 py-1 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
    dark ? 'text-slate-600' : 'text-slate-400'}`
  const td = `px-2 py-1 text-[10px] whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-600'}`

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setAbierto(v => !v)}
        className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
          dark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
      >
        <svg
          className={`w-3 h-3 transition-transform duration-150 ${abierto ? 'rotate-90' : ''}`}
          viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M5 11l4-4-4-4" />
        </svg>
        {t('salesOrders.documentos.control.title')}
      </button>

      {abierto && (
        <div className="mt-2 overflow-x-auto">
          <p className={`text-[10px] mb-1.5 ${mutedTxt}`}>{t('salesOrders.documentos.control.nota')}</p>
          <table className="w-full min-w-[620px] border-collapse text-left">
            <thead>
              <tr className={`border-b ${divider}`}>
                <th className={th}>{t('salesOrders.documentos.contenido.articulo')}</th>
                <th className={th}>{t('salesOrders.documentos.control.tipoPalet')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.documentos.control.tara')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.documentos.control.cajasPalet')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.documentos.control.paletsEstimados')}</th>
                <th className={th}>{t('salesOrders.documentos.control.origenUdsCaja')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.documentos.control.netoTotal')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.documentos.control.brutoTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={`${claveLinea(l)}-ctrl-${i}`} className={`border-t ${divider}`}>
                  <td className={`${td} font-mono`}>{l.articulo}</td>
                  <Celda td={td} dark={dark} valor={l.tipoPaletAplicado ?? null} />
                  <Celda td={td} dark={dark} valor={fmtKg(l.pesoPalet)} alinear="right" />
                  <Celda td={td} dark={dark} valor={fmtEnt(l.cajasPorPalet)} alinear="right" />
                  <Celda td={td} dark={dark} valor={fmtEnt(l.paletsEstimados)} alinear="right" />
                  <Celda td={td} dark={dark} valor={l.unCajaOrigen ?? null} />
                  <Celda td={td} dark={dark} valor={fmtKg(l.pesoNetoTotal)} alinear="right" />
                  <Celda td={td} dark={dark} valor={fmtKg(l.pesoBrutoTotal)} alinear="right" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}



// ── Formulario ────────────────────────────────────────────────────────────────


// ── Panel de previsualización ────────────────────────────────────────────────

function PanelPreview({
  dark, pdf, regenerando, onVolver, onRegenerar,
}: {
  dark: boolean
  pdf: DocumentoGenerado
  regenerando: boolean
  onVolver: () => void
  /** Ausente = este tipo de documento no se regenera desde aquí. El botón NO se
   *  pinta: ver el porqué donde se decide, en el montaje de este panel. */
  onRegenerar?: () => void
}) {
  const { t } = useTranslation()
  const border = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const previewBg = dark ? 'bg-[#070a0f]' : 'bg-slate-100'

  // El blob se crea una vez por PDF y se revoca al cambiar o al desmontar: sin el
  // revoke, cada regeneración deja el anterior en memoria del navegador.
  const objectUrl = useMemo(() => {
    try { return URL.createObjectURL(base64ToBlob(pdf.contenido, pdf.mime || MIME_PDF)) }
    catch { return null }
  }, [pdf.contenido, pdf.mime])

  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }, [objectUrl])

  const secundario = `px-3 py-2 rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
    dark ? 'text-slate-300 hover:bg-white/6' : 'text-slate-600 hover:bg-slate-100'}`

  return (
    <>
      <div className={`flex-1 min-h-0 ${previewBg}`}>
        {regenerando
          ? <div className="h-full flex items-center justify-center">
              <Loader label={t('salesOrders.documentos.generando')} />
            </div>
          : objectUrl
            ? <iframe src={objectUrl} title={pdf.nombreArchivo} className="w-full h-full border-0" />
            : <div className="h-full flex items-center justify-center px-8">
                <p className={`text-[12px] text-center ${mutedTxt}`}>
                  {t('salesOrders.documentos.errorPreview')}
                </p>
              </div>}
      </div>

      <div className={`flex items-center gap-2 px-6 py-3.5 border-t ${border} shrink-0`}>
        <p className={`text-[11px] flex-1 min-w-0 truncate ${mutedTxt}`}>
          <span className="font-mono">{pdf.nombreArchivo}</span>
          {pdf.generacionMs > 0 && (
            <span className={dark ? 'text-slate-600' : 'text-slate-400'}>
              {' '}· {t('salesOrders.documentos.renderMs', { ms: pdf.generacionMs })}
            </span>
          )}
        </p>
        <button type="button" onClick={onVolver} className={secundario}>
          {t('salesOrders.documentos.volver')}
        </button>
        {onRegenerar && (
          <button type="button" onClick={onRegenerar} className={secundario}>
            {t('salesOrders.documentos.regenerar')}
          </button>
        )}
        {/* La descarga usa su PROPIO blob, no el del iframe.
            Compartirlo parecía lo elegante y ata dos cosas que no tienen por qué
            vivir lo mismo: el visor de PDF corre en otro proceso, y cualquier
            cosa que le pase a su URL —o un desmontaje del panel que dispare el
            revoke del useEffect— se lleva por delante una descarga en curso, que
            el navegador reporta como «error de red».
            El blob propio se libera con margen (ver utils/base64.ts), así que
            tampoco muere mientras el usuario elige carpeta. */}
        <button
          type="button"
          onClick={() => descargarBase64(pdf.contenido, pdf.nombreArchivo, pdf.mime || MIME_PDF)}
          className="px-4 py-2 rounded-lg text-[12px] font-semibold bg-sky-600 hover:bg-sky-500 text-white transition-colors cursor-pointer"
        >
          {t('salesOrders.documentos.descargar')}
        </button>
      </div>
    </>
  )
}

// ── Piezas pequeñas ──────────────────────────────────────────────────────────






// ── Errores ──────────────────────────────────────────────────────────────────

